import { useEffect, useMemo, useState } from 'react';
import { Calculator, Pencil } from 'lucide-react';
import { NumberField } from '@renderer/components/ui/NumberField';
import { pesosACentavos, centavosAPesos } from '@shared/centavos';

/**
 * Sprint 20 — Contador de billetes y monedas (denominaciones MXN).
 *
 * Reemplaza el NumberField libre del "efectivo contado físicamente" por un
 * wizard de denominaciones. Reduce error de suma humano al cuadrar caja.
 *
 * Modos:
 *   - "billetes" (por defecto): inputs por denominación + monedas libre.
 *   - "directo": campo libre tipo monto total (modo legacy, para usuarios
 *     que prefieren contar mentalmente y escribir el total).
 *
 * El componente emite el TOTAL calculado a `onChange` siempre que cambia
 * cualquier input. El padre persiste sólo el total (no las denominaciones).
 */

interface Denominacion {
  valor: number;
  label: string;
}

// Billetes MXN. Excluyo $1000 (raro en operación) y dejo $20 como el más chico.
const BILLETES: Denominacion[] = [
  { valor: 500, label: '$500' },
  { valor: 200, label: '$200' },
  { valor: 100, label: '$100' },
  { valor: 50,  label: '$50'  },
  { valor: 20,  label: '$20'  },
];

interface Props {
  monedaSimbolo: string;
  value: number;
  onChange: (totalMonto: number) => void;
}

export function ContadorBilletes({ monedaSimbolo, value, onChange }: Props) {
  const [modo, setModo] = useState<'billetes' | 'directo'>('billetes');
  // Cantidad de cada billete (Map<valor, cantidad>).
  const [conteos, setConteos] = useState<Map<number, number>>(new Map());
  const [monedasMonto, setMonedasMonto] = useState<number>(0);

  // Inicialización: si llega `value` pero conteos vacíos, queda en modo directo (no podemos descomponer).
  // Si el usuario tipea en modo directo, refleja value directo; el callback solo se llama en interacciones.
  // Sprint 39 — `value` y `onChange` son CENTAVOS (Int). Internamente trabajamos
  // billetes/monedas en pesos (porque sus denominaciones son enteros en pesos)
  // y solo convertimos en el boundary.
  useEffect(() => {
    if (modo === 'directo') return;
    // En modo billetes, recompute total cada vez que cambian conteos o monedas.
    let total = 0;
    for (const [valor, n] of conteos.entries()) total += valor * n;
    total += monedasMonto;
    const totalCentavos = pesosACentavos(total);
    // Solo notificar si difiere para evitar loops.
    if (totalCentavos !== value) onChange(totalCentavos);
  }, [conteos, monedasMonto, modo]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalBilletes = useMemo(() => {
    let t = 0;
    for (const [valor, n] of conteos.entries()) t += valor * n;
    return t;
  }, [conteos]);

  const totalGeneral = totalBilletes + monedasMonto;

  const setCantidad = (valor: number, cantidad: number) => {
    const next = new Map(conteos);
    if (cantidad <= 0) next.delete(valor);
    else next.set(valor, cantidad);
    setConteos(next);
  };

  return (
    <div className="space-y-3">
      {/* Toggle de modo */}
      <div className="flex items-center gap-1 text-xs">
        <button
          type="button"
          /*
            Sprint 118 — 44 px, como todo lo que se aprieta (Fitts).

            Medían 26. Son los dos botones que deciden CÓMO se cuenta el dinero del día, en la
            pantalla del dinero, y los usa a diario alguien que apenas está aprendiendo el sistema.
            Un barrido de las 20 pantallas los encontró: a ojo no se ven mal, se ven "compactos".
          */
          onClick={() => setModo('billetes')}
          className={`flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition ${
            modo === 'billetes'
              ? 'bg-primary text-white border-primary'
              : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
          }`}
        >
          <Calculator className="h-4 w-4" />
          Contar billetes
        </button>
        <button
          type="button"
          onClick={() => setModo('directo')}
          className={`flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition ${
            modo === 'directo'
              ? 'bg-primary text-white border-primary'
              : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
          }`}
        >
          <Pencil className="h-4 w-4" />
          Escribir total
        </button>
      </div>

      {modo === 'billetes' ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            Billetes
          </div>
          <div className="space-y-1">
            {BILLETES.map((b) => {
              const cant = conteos.get(b.valor) ?? 0;
              const sub = cant * b.valor;
              return (
                <div key={b.valor} className="grid grid-cols-[50px_1fr_80px] gap-2 items-center sm:grid-cols-[60px_1fr_90px]">
                  <span className="font-mono font-semibold text-slate-700">{b.label}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setCantidad(b.valor, Math.max(0, cant - 1))}
                      className="w-7 h-7 rounded border border-slate-300 hover:bg-slate-100 text-sm font-bold"
                      /*
                        Sprint 120 — Sin esto, un lector de pantalla anuncia tres controles seguidos
                        como "menos", "cuadro de texto en blanco", "más", sin decir NUNCA de qué
                        billete. La denominación está a la izquierda y en pantalla se entiende sola;
                        para quien no la ve, la fila entera era indistinguible de las otras cuatro.
                      */
                      aria-label={`Un billete de ${b.label} menos`}
                      disabled={cant <= 0}
                    >
                      −
                    </button>
                    <NumberField
                      min={0}
                      decimals={0}
                      className="input text-center font-mono text-sm h-8 w-16"
                      ariaLabel={`Cuántos billetes de ${b.label}`}
                      value={cant}
                      onChange={(n) => setCantidad(b.valor, n)}
                    />
                    <button
                      type="button"
                      onClick={() => setCantidad(b.valor, cant + 1)}
                      className="w-7 h-7 rounded border border-slate-300 hover:bg-slate-100 text-sm font-bold"
                      aria-label={`Un billete de ${b.label} más`}
                    >
                      +
                    </button>
                  </div>
                  <span className="text-right font-mono text-sm text-slate-600">
                    = {monedaSimbolo}{sub.toFixed(0)}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="border-t border-slate-200 pt-2 mt-2">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">
              Monedas (suma libre)
            </div>
            <div className="grid grid-cols-[60px_1fr_90px] gap-2 items-center">
              <span className="font-mono text-xs text-slate-600">Total</span>
              <NumberField
                min={0}
                decimals={2}
                className="input text-right font-mono text-sm h-8"
                /*
                  Su rótulo visible dice sólo "Total", que fuera de contexto no distingue este campo
                  del total general de la caja — y son cosas distintas: aquí van únicamente las
                  monedas sueltas.
                */
                ariaLabel="Total en monedas sueltas"
                value={monedasMonto}
                onChange={setMonedasMonto}
              />
              <span className="text-right font-mono text-sm text-slate-600">
                = {monedaSimbolo}{monedasMonto.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="border-t-2 border-slate-300 pt-2 mt-3 flex items-baseline justify-between">
            <span className="font-bold text-slate-800">TOTAL contado</span>
            <span className="font-mono font-bold text-2xl text-primary">
              {monedaSimbolo}{totalGeneral.toFixed(2)}
            </span>
          </div>
        </div>
      ) : (
        <div>
          <NumberField
            min={0}
            decimals={2}
            className="input text-2xl font-mono text-right"
            value={centavosAPesos(value)}
            onChange={(n) => onChange(pesosACentavos(n))}
          />
          <p className="text-xs text-slate-500 mt-1">
            Escribe el total ya contado mentalmente o con calculadora.
          </p>
        </div>
      )}
    </div>
  );
}
